/* =====================================================
   assets/hero-banner-2026.js
   NuFace 2026 Redesign — Hero / Slideshow controller
   MS 2026-05-18

   Loaded only when section has 2+ slides. Handles:
   - Prev/next navigation
   - Optional autoplay with configurable speed
   - Pause/play toggle button
   - Pause on hover
   - Keyboard nav (ArrowLeft / ArrowRight when section focused)
   - Pauses when section is offscreen (perf + battery)
   - Re-initializes inside Shopify theme editor
   ===================================================== */

(function () {
  "use strict";

  function initHero(section) {
    var slides = section.querySelectorAll("[data-hero-slide]");
    if (slides.length < 2) return;
    if (section.dataset.heroInited === "1") return;
    section.dataset.heroInited = "1";

    var prevBtn = section.querySelector("[data-hero-prev]");
    var nextBtn = section.querySelector("[data-hero-next]");
    var pauseBtn = section.querySelector("[data-hero-pause]");
    var autoplayMs = parseInt(section.dataset.autoplay, 10) || 0;
    var pauseOnHover = section.dataset.pauseOnHover === "true";

    var currentIndex = 0;
    var autoplayTimer = null;
    var isPaused = false;

    // KC 2026-06-26: the controls are now anchored entirely in CSS — on mobile they're part of
    // the bottom-anchored group inside the card (CSS `bottom`), which tracks the card height as
    // it shrinks to clear the chat bar. The old logic here pinned them via an inline `top`
    // (= image height − 48), which overrode the CSS and broke that tracking. We now only CLEAR
    // any stale inline `top` (from a prior build or a theme-editor re-init) so CSS always wins.
    function positionControls() {
      [prevBtn, nextBtn, pauseBtn].forEach(function (el) {
        if (el) el.style.top = "";
      });
    }

    // Respect prefers-reduced-motion: start paused so the hero never auto-advances
    // for those users. Arrows still work, and the pause/play toggle lets them opt in.
    var motionQuery = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
    function prefersReducedMotion() {
      return !!(motionQuery && motionQuery.matches);
    }

    if (prefersReducedMotion()) {
      isPaused = true;
      if (pauseBtn) {
        pauseBtn.setAttribute("aria-pressed", "true");
        pauseBtn.setAttribute("aria-label", "Play slideshow");
      }
    }

    if (motionQuery) {
      var onMotionChange = function () {
        if (prefersReducedMotion()) {
          isPaused = true;
          stopAutoplay();
          if (pauseBtn) {
            pauseBtn.setAttribute("aria-pressed", "true");
            pauseBtn.setAttribute("aria-label", "Play slideshow");
          }
        }
      };
      if (motionQuery.addEventListener) {
        motionQuery.addEventListener("change", onMotionChange);
      } else if (motionQuery.addListener) {
        motionQuery.addListener(onMotionChange);
      }
    }

    function goToSlide(index) {
      slides[currentIndex].classList.remove("is-active");
      slides[currentIndex].setAttribute("aria-hidden", "true");
      currentIndex = (index + slides.length) % slides.length;
      slides[currentIndex].classList.add("is-active");
      slides[currentIndex].setAttribute("aria-hidden", "false");
    }
    function next() {
      goToSlide(currentIndex + 1);
    }
    function prev() {
      goToSlide(currentIndex - 1);
    }

    function startAutoplay() {
      if (autoplayMs <= 0 || isPaused) return;
      stopAutoplay();
      autoplayTimer = setInterval(next, autoplayMs);
    }
    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }
    function restartAutoplay() {
      if (!isPaused) {
        stopAutoplay();
        startAutoplay();
      }
    }

    if (prevBtn)
      prevBtn.addEventListener("click", function () {
        prev();
        restartAutoplay();
      });
    if (nextBtn)
      nextBtn.addEventListener("click", function () {
        next();
        restartAutoplay();
      });

    if (pauseBtn) {
      pauseBtn.addEventListener("click", function () {
        isPaused = !isPaused;
        pauseBtn.setAttribute("aria-pressed", String(isPaused));
        pauseBtn.setAttribute(
          "aria-label",
          isPaused ? "Play slideshow" : "Pause slideshow",
        );
        if (isPaused) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      });
    }

    if (pauseOnHover) {
      section.addEventListener("mouseenter", stopAutoplay);
      section.addEventListener("mouseleave", function () {
        if (!isPaused) startAutoplay();
      });
    }

    section.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") {
        prev();
        restartAutoplay();
      } else if (e.key === "ArrowRight") {
        next();
        restartAutoplay();
      }
    });

    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!isPaused) startAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.1 },
      );
      io.observe(section);
    } else {
      startAutoplay();
    }

    // Anchor the mobile controls to the image bottom (exact, scrollbar-proof) and keep
    // them there as the viewport changes or the hero image finishes loading.
    positionControls();
    window.addEventListener("resize", positionControls);
    window.addEventListener("load", positionControls);
    var heroImg = section.querySelector(".hero-2026__image");
    if (heroImg && !heroImg.complete) {
      heroImg.addEventListener("load", positionControls);
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll(".hero-2026[data-slides-count]");
    for (var i = 0; i < sections.length; i++) {
      var count = parseInt(sections[i].dataset.slidesCount, 10) || 0;
      if (count > 1) initHero(sections[i]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Re-init when a section is added/edited in the theme editor */
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
})();
